import React, { ChangeEvent, useEffect, useState } from 'react'
import styled from 'styled-components'
import { IoClose } from 'react-icons/io5'
import { useParams } from 'react-router-dom'
import axios from 'axios'
import { Chip } from '@mui/material'
import { toast } from 'react-toastify'
import { uniqueId } from 'lodash'

interface AddKnowledgeSourceModalProps {
  isOpen: boolean
  onClose: () => void
  getInformation: any
  getInformationDetails: any
}

const AddKnowledgeSourceModal: React.FC<AddKnowledgeSourceModalProps> = ({
  isOpen,
  onClose,
  getInformation,
  getInformationDetails
}) => {
  const [selectedType, setSelectedType] = useState<'webpage' | 'file' | 'table'>('webpage')
  const [publicUrl, setPublicUrl] = useState('')
  const [selectedFile, setSelectedFile] = useState<any>(null)
  const [allKbFiles, setAllKbFiles] = useState<any>([])
  const [tableData, setTableData] = useState<string>('')

  // Database configuration state
  const [dbType, setDbType] = useState<string>('')
  const [dbHost, setDbHost] = useState<string>('')
  const [dbPort, setDbPort] = useState<number | ''>('')
  const [dbDatabase, setDbDatabase] = useState<string>('')
  const [dbUsername, setDbUsername] = useState<string>('')
  const [dbPassword, setDbPassword] = useState<string>('')
  const [dbSchemaNotes, setDbSchemaNotes] = useState<string>('')

  // Connected database state
  const [connectedDb, setConnectedDb] = useState<any>(null)

  // Validation errors state
  const [dbErrors, setDbErrors] = useState<{
    dbType?: string
    dbHost?: string
    dbPort?: string
    dbDatabase?: string
    dbUsername?: string
    dbPassword?: string
  }>({})

  // Loading state
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const { uuid } = useParams()

  const botId = uuid // Replace with actual bot ID
  const baseUrl = process.env.REACT_APP_RESUME_PARSER_URL // Replace with your base API URL
  const token = localStorage.getItem('auth-token') // Replace with your auth token if required

  console.log(getInformation, 'shakya')
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      setSelectedFile(e.target.files[0])
    }
  }

  // Validate database configuration
  const validateDbConfig = (): boolean => {
    const errors: typeof dbErrors = {}

    if (!dbType.trim()) {
      errors.dbType = 'Database type is required'
    }

    if (!dbHost.trim()) {
      errors.dbHost = 'Host is required'
    }

    if (!dbPort) {
      errors.dbPort = 'Port is required'
    } else if (dbPort < 1 || dbPort > 65535) {
      errors.dbPort = 'Port must be between 1 and 65535'
    }

    if (!dbDatabase.trim()) {
      errors.dbDatabase = 'Database name is required'
    }

    if (!dbUsername.trim()) {
      errors.dbUsername = 'Username is required'
    }

    if (!dbPassword.trim()) {
      errors.dbPassword = 'Password is required'
    }

    setDbErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async () => {
    // Validate for table type before starting
    if (selectedType === 'table' && !validateDbConfig()) {
      return
    }

    const url = `${baseUrl}/ai-chatbots/knowledge-base/${botId}`
    const urlTable = `${baseUrl}/ai-chatbots/db-connect/${botId}`
    setIsLoading(true)

    try {
      let response

      if (selectedType === 'file' && selectedFile) {
        const formData = new FormData()
        formData.append('source_type', 'file')
        formData.append('file', selectedFile)

        response = await axios.post(url, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`
          }
        })
      }

      if (selectedType === 'webpage') {
        const formData = new FormData()
        formData.append('source_type', 'url')
        formData.append('url', publicUrl)

        response = await axios.post(url, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`
          }
        })
      }

      if (selectedType === 'table') {
        const params = new URLSearchParams()
        params.append('db_type', dbType)
        params.append('host', dbHost)
        params.append('port', String(dbPort))
        params.append('database', dbDatabase)
        params.append('username', dbUsername)
        params.append('password', dbPassword)
        params.append('schema_notes', dbSchemaNotes)

        response = await axios.post(urlTable, params.toString(), {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            Authorization: `Bearer ${token}`
          }
        })
      }
      console.log(response, 'shakyaResponse')
      if (response?.data?.status === 'success' || response?.data?.status === 200) {
        if (selectedType === 'table') {
          // Set connectedDb immediately after successful connection
          setConnectedDb({
            id: response?.data?.connection_id || null,
            database_name: dbDatabase,
            db_type: dbType,
            host: dbHost,
            port: dbPort,
            username: dbUsername
          })
          // Clear form fields after successful connection
          setDbType('')
          setDbHost('')
          setDbPort('')
          setDbDatabase('')
          setDbUsername('')
          setDbPassword('')
          setDbSchemaNotes('')
          setDbErrors({})
          toast.success(`Connected to database: ${dbDatabase}`)
          // Don't close modal, show the chip instead
        } else {
          toast.success('Knowledge source added successfully.')
          onClose()
        }
        getInformationDetails() // Refresh data from backend
      } else {
        // Show API error message if available, otherwise show generic message
        const errorMessage =
          response?.data?.error || 'Failed to add knowledge source. Please try again.'
        toast.error(errorMessage)
      }

      console.log('Success:', response?.data)
    } catch (error) {
      console.error('Submission Error:', error)
      toast.error('Failed to add knowledge source. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }
  const handleDelete = async (selectedKb: any) => {
    const kbId = selectedKb?.id // Use the passed `selectedKbId` directly
    const chatbotId = selectedKb?.chatbot_id

    // Ensure both kbId and chatbotId are valid before proceeding
    if (!kbId || !chatbotId) {
      console.error('Invalid kbId or chatbotId')
      return
    }

    const deleteUrl = `${baseUrl}/ai-chatbots/knowledge-base/delete/${chatbotId}/${kbId}`

    try {
      // Perform the delete request
      const response = await axios.delete(deleteUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
          accept: 'application/json',
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      })

      if (response.status === 200) {
        // Clear related UI state after successful deletion
        setSelectedFile(null)
        setPublicUrl('')
        setTableData('')
        getInformationDetails() // Refresh data from backend
        onClose() // Close modal
        toast.success('Knowledge source deleted successfully.')
      } else {
        console.error('Failed to delete knowledge source. Response:', response)
      }
    } catch (error) {
      console.error('Error deleting knowledge source:', error)
      // You can add additional user-friendly error handling or feedback here, if needed
    }
  }

  const handleDisconnectDb = async () => {
    if (!connectedDb?.id || !botId) {
      console.error('Invalid database connection or bot ID')
      return
    }

    try {
      const deleteUrl = `${baseUrl}/ai-chatbots/db-connection/${botId}/${connectedDb.id}`
      const response = await axios.delete(deleteUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
          accept: 'application/json',
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      })

      if (response.status === 200 || response.data?.status === 'success') {
        setConnectedDb(null)
        // Clear form fields
        setDbType('')
        setDbHost('')
        setDbPort('')
        setDbDatabase('')
        setDbUsername('')
        setDbPassword('')
        setDbSchemaNotes('')
        getInformationDetails() // Refresh data from backend
        toast.success('Database disconnected successfully.')
      } else {
        console.error('Failed to disconnect database. Response:', response)
        toast.error('Failed to disconnect database.')
      }
    } catch (error: any) {
      console.error('Error disconnecting database:', error)
      toast.error(error?.response?.data?.error || 'Failed to disconnect database.')
    }
  }

  useEffect(() => {
    if (getInformation) {
      if (getInformation?.kb_data?.length > 0) {
        setAllKbFiles(getInformation?.kb_data)
      }
      setPublicUrl(getInformation?.kb_data[0]?.file ? getInformation?.kb_data[0]?.file : '')

      // Load connected database info
      if (getInformation?.db_data) {
        console.log('[AddKnowledgeSourceModal] Database connected:', getInformation.db_data)
        setConnectedDb(getInformation.db_data)
      } else {
        console.log('[AddKnowledgeSourceModal] No database connected')
        setConnectedDb(null)
      }
    }
  }, [getInformation])

  // When modal opens, refresh information to get latest database connection status
  useEffect(() => {
    if (isOpen) {
      getInformationDetails()
    }
  }, [isOpen])

  console.log('allKbFiles', allKbFiles)

  if (!isOpen) return null

  return (
    <ModalOverlay>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Add knowledge source</ModalTitle>
          <CloseButton onClick={onClose}>
            <IoClose size={20} />
          </CloseButton>
        </ModalHeader>
        <InfoBar>
          <span></span>
        </InfoBar>
        <SectionTitle>Type</SectionTitle>
        <TypeOptions>
          <TypeOption
            active={selectedType === 'webpage'}
            onClick={() => setSelectedType('webpage')}
          >
            <TypeIcon>🌐</TypeIcon>
            Webpage
          </TypeOption>
          <TypeOption active={selectedType === 'file'} onClick={() => setSelectedType('file')}>
            <TypeIcon>📄</TypeIcon>
            File
          </TypeOption>
          <TypeOption active={selectedType === 'table'} onClick={() => setSelectedType('table')}>
            <TypeIcon>🔄</TypeIcon>
            Database
          </TypeOption>
        </TypeOptions>
        {selectedType === 'webpage' && (
          <>
            <FormGroup>
              <Label>Public URL</Label>
              <Input
                type='text'
                placeholder=''
                value={publicUrl}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setPublicUrl(e.target.value)}
                disabled={selectedType !== 'webpage'}
              />
            </FormGroup>
            {/* <CheckboxGroup>
              <input
                type='checkbox'
                checked={crawlAll}
                onChange={(e) => setCrawlAll(e.target.checked)}
                id='crawlAll'
              />
              <label htmlFor='crawlAll'>Crawl this link for all subpages</label>
            </CheckboxGroup> */}
            {allKbFiles && allKbFiles.length > 0 && (
              <div className='mt-2 ' style={{ marginLeft: '25px' }}>
                {allKbFiles.map(
                  (fileName: any, index: any) =>
                    fileName?.file_url?.length > 0 && ( // Check if file_name has content
                      <div key={uniqueId()} className='d-flex align-items-center mt-2'>
                        <Chip
                          label={fileName.file_url}
                          onDelete={() => handleDelete(fileName)}
                          color='primary'
                          variant='outlined'
                        />
                      </div>
                    )
                )}
              </div>
            )}
          </>
        )}
        {selectedType === 'file' && (
          <>
            <FormGroup>
              <Label>File</Label>
              <input
                type='file'
                id='file-upload'
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              <BrowseButton htmlFor='file-upload'>Browse files</BrowseButton>
              {selectedFile && (
                <div className='mt-2'>
                  <Chip
                    label={selectedFile.name}
                    onDelete={() => setSelectedFile(null)} // Implement the delete function
                    color='primary'
                    variant='outlined'
                  />
                </div>
              )}
              {allKbFiles && allKbFiles.length > 0 && (
                <div className='mt-2'>
                  {allKbFiles.map(
                    (fileName: any, index: any) =>
                      fileName?.file_name?.length > 0 && ( // Check if file_name has content
                        <div key={uniqueId()} className='d-flex align-items-center mt-2'>
                          <Chip
                            label={fileName.file_name}
                            onDelete={() => handleDelete(fileName)}
                            color='primary'
                            variant='outlined'
                          />
                        </div>
                      )
                  )}
                </div>
              )}
            </FormGroup>
          </>
        )}

        {selectedType === 'table' && (
          <>
            {connectedDb ? (
              // Show connected database as chip (exactly like file chip)
              <FormGroup>
                <Label>Database</Label>
                <div className='mt-2'>
                  <Chip
                    label={connectedDb.database_name}
                    onDelete={handleDisconnectDb}
                    color='primary'
                    variant='outlined'
                  />
                </div>
              </FormGroup>
            ) : (
              // Show connection form
              <>
                <FormGroup>
                  <Label>
                    Database Type <RequiredMark>*</RequiredMark>
                  </Label>
                  <Select
                    value={dbType}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                      setDbType(e.target.value)
                      if (dbErrors.dbType) setDbErrors((prev) => ({ ...prev, dbType: undefined }))
                    }}
                    hasError={!!dbErrors.dbType}
                  >
                    <option value=''>Select database type</option>
                    <option value='postgresql'>PostgreSQL</option>
                    <option value='mysql'>MySQL</option>
                    <option value='mssql'>Microsoft SQL Server</option>
                    <option value='oracle'>Oracle</option>
                    <option value='sqlite'>SQLite</option>
                    <option value='mongodb'>MongoDB</option>
                  </Select>
                  {dbErrors.dbType && <ErrorMessage>{dbErrors.dbType}</ErrorMessage>}
                </FormGroup>
                <FormGroup>
                  <Label>
                    Host <RequiredMark>*</RequiredMark>
                  </Label>
                  <Input
                    type='text'
                    placeholder='e.g. localhost or 192.168.1.1'
                    value={dbHost}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      setDbHost(e.target.value)
                      if (dbErrors.dbHost) setDbErrors((prev) => ({ ...prev, dbHost: undefined }))
                    }}
                    hasError={!!dbErrors.dbHost}
                  />
                  {dbErrors.dbHost && <ErrorMessage>{dbErrors.dbHost}</ErrorMessage>}
                </FormGroup>
                <FormGroup>
                  <Label>
                    Port <RequiredMark>*</RequiredMark>
                  </Label>
                  <Input
                    type='number'
                    placeholder='e.g. 5432'
                    value={dbPort}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      setDbPort(e.target.value ? parseInt(e.target.value) : '')
                      if (dbErrors.dbPort) setDbErrors((prev) => ({ ...prev, dbPort: undefined }))
                    }}
                    hasError={!!dbErrors.dbPort}
                  />
                  {dbErrors.dbPort && <ErrorMessage>{dbErrors.dbPort}</ErrorMessage>}
                </FormGroup>
                <FormGroup>
                  <Label>
                    Database <RequiredMark>*</RequiredMark>
                  </Label>
                  <Input
                    type='text'
                    placeholder='Database name'
                    value={dbDatabase}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      setDbDatabase(e.target.value)
                      if (dbErrors.dbDatabase)
                        setDbErrors((prev) => ({ ...prev, dbDatabase: undefined }))
                    }}
                    hasError={!!dbErrors.dbDatabase}
                  />
                  {dbErrors.dbDatabase && <ErrorMessage>{dbErrors.dbDatabase}</ErrorMessage>}
                </FormGroup>
                <FormGroup>
                  <Label>
                    Username <RequiredMark>*</RequiredMark>
                  </Label>
                  <Input
                    type='text'
                    placeholder='Database username'
                    value={dbUsername}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      setDbUsername(e.target.value)
                      if (dbErrors.dbUsername)
                        setDbErrors((prev) => ({ ...prev, dbUsername: undefined }))
                    }}
                    hasError={!!dbErrors.dbUsername}
                  />
                  {dbErrors.dbUsername && <ErrorMessage>{dbErrors.dbUsername}</ErrorMessage>}
                </FormGroup>
                <FormGroup>
                  <Label>
                    Password <RequiredMark>*</RequiredMark>
                  </Label>
                  <Input
                    type='password'
                    placeholder='Database password'
                    value={dbPassword}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      setDbPassword(e.target.value)
                      if (dbErrors.dbPassword)
                        setDbErrors((prev) => ({ ...prev, dbPassword: undefined }))
                    }}
                    hasError={!!dbErrors.dbPassword}
                  />
                  {dbErrors.dbPassword && <ErrorMessage>{dbErrors.dbPassword}</ErrorMessage>}
                </FormGroup>
                <FormGroup>
                  <Label>Schema notes (optional)</Label>
                  <TextArea
                    placeholder='Add notes about schema, important tables, joins, or business context'
                    value={dbSchemaNotes}
                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                      setDbSchemaNotes(e.target.value)
                    }
                  />
                </FormGroup>
              </>
            )}
          </>
        )}

        <ButtonRow>
          <CancelButton type='button' onClick={onClose} disabled={isLoading}>
            Cancel
          </CancelButton>
          {/* <DeleteButton type='button' onClick={handleDelete}>
            Delete
          </DeleteButton> */}
          <ContinueButton
            type='button'
            onClick={selectedType === 'table' && connectedDb ? onClose : handleSubmit}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Spinner /> Loading...
              </>
            ) : selectedType === 'table' && connectedDb ? (
              'Done'
            ) : selectedType === 'file' ? (
              'Add source'
            ) : selectedType === 'table' ? (
              'Connect Database'
            ) : (
              'Continue'
            )}
          </ContinueButton>
        </ButtonRow>
      </ModalContent>
    </ModalOverlay>
  )
}

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 1000;
  overflow-y: auto;
  padding: 24px 16px;
`

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  width: 100%;
  max-width: 480px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  padding: 0 0 24px 0;
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  margin: auto 0;
`

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px 0;
`

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
`

const CloseButton = styled.button`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  margin: -4px;
  &:hover {
    color: #1f2937;
  }
`

const InfoBar = styled.div`
  background: #fef9c3;
  color: #92400e;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  border-radius: 6px;
  margin: 20px 24px 16px 24px;
`

const UpgradeBtn = styled.button`
  background: #a78bfa;
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  padding: 2px 12px;
  cursor: pointer;
`

const SectionTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 24px 8px 24px;
`

const TypeOptions = styled.div`
  display: flex;
  gap: 12px;
  margin: 0 24px 20px 24px;
`

const TypeOption = styled.button<{ active: boolean }>`
  flex: 1;
  background: ${({ active }) => (active ? '#ede9fe' : '#f8fafc')};
  color: #1a1a1a;
  border: 1.5px solid ${({ active }) => (active ? '#7c3aed' : '#e5e7eb')};
  border-radius: 8px;
  padding: 16px 0 10px 0;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  transition: border 0.2s, background 0.2s;
`

const TypeIcon = styled.div`
  font-size: 22px;
  margin-bottom: 6px;
`

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 0 24px 12px 24px;
`

const Label = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: #1a1a1a;
`

const Input = styled.input<{ hasError?: boolean }>`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${({ hasError }) => (hasError ? '#ef4444' : '#e5e7eb')};
  border-radius: 6px;
  background-color: ${({ hasError }) => (hasError ? '#fef2f2' : '#f8fafc')};
  font-size: 14px;
  &:focus {
    outline: none;
    border-color: ${({ hasError }) => (hasError ? '#ef4444' : '#7c3aed')};
    background-color: white;
  }
`

const Select = styled.select<{ hasError?: boolean }>`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${({ hasError }) => (hasError ? '#ef4444' : '#e5e7eb')};
  border-radius: 6px;
  background-color: ${({ hasError }) => (hasError ? '#fef2f2' : '#f8fafc')};
  font-size: 14px;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  &:focus {
    outline: none;
    border-color: ${({ hasError }) => (hasError ? '#ef4444' : '#7c3aed')};
    background-color: white;
  }
`

const TextArea = styled.textarea`
  width: 100%;
  min-height: 90px;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background-color: ${({ hasError }) => (hasError ? '#fef2f2' : '#f8fafc')};
  font-size: 14px;
  resize: vertical;
  &:focus {
    outline: none;
    border-color: ${({ hasError }) => (hasError ? '#ef4444' : '#7c3aed')};
    background-color: white;
  }
`

const ErrorMessage = styled.span`
  color: #ef4444;
  font-size: 12px;
  margin-top: 2px;
`

const RequiredMark = styled.span`
  color: #ef4444;
  margin-left: 2px;
`

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 24px 12px 24px;
  font-size: 13px;
`

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin: 24px 24px 0 24px;
`

const CancelButton = styled.button`
  background: #f3f4f6;
  color: #1a1a1a;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  padding: 8px 24px;
  cursor: pointer;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const ContinueButton = styled.button`
  background-color: #a21caf;
  color: white;
  padding: 8px 24px;
  border-radius: 6px;
  border: none;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 120px;
  &:hover:not(:disabled) {
    background-color: #7c3aed;
  }
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`

const Spinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`
const DeleteButton = styled.button`
  background-color: #ff4d4f;
  color: white;
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  margin-left: 8px;
  &:hover {
    background-color: #ff7875;
  }
`

const BrowseButton = styled.label`
  background-color: #a21caf;
  color: white;
  padding: 8px 18px;
  border-radius: 6px;
  border: none;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: inline-block;
  margin-bottom: 8px;
`

const FileNote = styled.div`
  font-size: 13px;
  color: #6b7280;
`
const SelectedFileWrapper = styled.div`
  margin-top: 10px;
  background-color: #f1f1f1;
  padding: 6px 10px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 300px;
`

const DeleteIcon = styled.button`
  background: none;
  border: none;
  color: #ff4d4f;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 0;
`

export default AddKnowledgeSourceModal
